import { buildSignedRecordPath, coerceRegisterRevision } from '../safetyCases/workspaceRegisters';
import { buildSafetyReportArtifactPath } from '../../utils/reportFlow';

export const SIGNED_RECORD_NOTICE = 'Safety Scan signed. Opening the signed record.';
export const SIGNED_ARTIFACT_NOTICE = 'Safety Scan signed. Opening the signed artifact.';

export type PostFinalizeNavigation = {
  path: string;
  notice: string;
  usedExactRevision: boolean;
};

export function resolvePostFinalizeRevision(source: {
  revision?: unknown;
  report_revision?: unknown;
  current_revision?: unknown;
  revisions?: Array<{ revision?: unknown }> | null;
} | null | undefined): number | null {
  const revision = source?.revision ?? source?.report_revision ?? source?.current_revision;
  return coerceRegisterRevision({
    revision: typeof revision === 'number' || typeof revision === 'string' ? revision : null,
    revisions: Array.isArray(source?.revisions)
      ? source?.revisions.map((entry) => ({
        revision: typeof entry?.revision === 'number' ? entry.revision : null,
      }))
      : undefined,
  });
}

export function resolvePostFinalizeNavigation(args: {
  reportId?: string | null;
  revision?: unknown;
  searchParams: URLSearchParams;
  artifactArgs?: Parameters<typeof buildSafetyReportArtifactPath>[0];
}): PostFinalizeNavigation {
  const reportId = String(args.reportId || '').trim();
  const revision = resolvePostFinalizeRevision({ revision: args.revision });
  if (reportId && revision != null) {
    const path = buildSignedRecordPath({ reportId, revision }, args.searchParams);
    if (path) {
      return { path, notice: SIGNED_RECORD_NOTICE, usedExactRevision: true };
    }
  }
  const artifactPath = buildSafetyReportArtifactPath({
    ...(args.artifactArgs || {}),
    reportId: reportId || String(args.artifactArgs?.reportId || '').trim(),
  });
  return {
    path: artifactPath,
    notice: SIGNED_ARTIFACT_NOTICE,
    usedExactRevision: false,
  };
}

/** Buyer Scan signing: open the Signed Record only when the exact revision is known. */
export function resolveBuyerPostFinalizeNavigation(args: {
  reportId?: string | null;
  revision?: unknown;
  searchParams: URLSearchParams;
}): PostFinalizeNavigation | null {
  const reportId = String(args.reportId || '').trim();
  const revision = resolvePostFinalizeRevision({ revision: args.revision });
  if (!reportId || revision == null) return null;
  const path = buildSignedRecordPath({ reportId, revision }, args.searchParams);
  if (!path) return null;
  return { path, notice: SIGNED_RECORD_NOTICE, usedExactRevision: true };
}

export function isRevisionlessSignedRecordPath(path: string): boolean {
  return /\/acceptance\/reports\/records\/[^/]+\/?(?:\?|$)/.test(path)
    && !/\/revisions\/\d+/.test(path);
}
